import { useMemo, useState, type ReactElement } from 'react';
import {
  Track,
  darunterMoeglich,
  freieStelle,
  konfliktBei,
  moeglicheStellen,
  sortedClips,
  spurIndexFuer,
  stelleFuer,
  Clip,
  type Op,
  type Stelle,
} from '@schnitt/shared';
import { ausDatei, useEditor } from '../store';
import { newId } from '../ids';
import { useZeitText } from '../zeit';
import { Sheet } from './Sheet';

/**
 * Wohin das ausgesuchte Medium kommt.
 *
 * „Beim + auf Play Linie müssen wir Medien auswählen, dann fragen wo hin.
 * Aktuelle Position / davor oder danach, und Ebene. Was tun bei Konflikten:
 * Ebene drüber oder drunter“ (Sascha, 7. September).
 *
 * Vorher fiel diese Entscheidung stillschweigend: an den Abspielkopf, und war
 * dort kein Platz, in die nächstgelegene Lücke. Das wurde ehrlich gemeldet,
 * war aber geraten — und beim Schneiden ist „ungefähr dort“ die Sorte Antwort,
 * die man hinterher von Hand korrigiert.
 *
 * Gerechnet wird nichts hier: die Regeln stehen in `@schnitt/shared/einfuegen`
 * und sind dort geprüft. Dieses Blatt zeigt sie nur — mit den Zahlen, die
 * dabei herauskommen, damit man vor dem Tippen sieht, was passieren wird.
 */
export function EinfuegenSheet({ onClose }: { onClose: () => void }): ReactElement | null {
  const zeit = useZeitText();
  const timeline = useEditor((s) => s.timeline);
  const playhead = useEditor((s) => s.playhead);
  const dispatch = useEditor((s) => s.dispatch);
  const select = useEditor((s) => s.select);
  const toast = useEditor((s) => s.toast);
  const medium = useEditor((s) => s.einfuegeMedium);
  const setEinfuegeMedium = useEditor((s) => s.setEinfuegeMedium);
  const setEinfuegeStelle = useEditor((s) => s.setEinfuegeStelle);

  const fps = timeline.settings.fps;
  const istTon = medium?.kind === 'audio';

  /** Die Ebenen, die dieses Medium aufnehmen können -- in der Reihenfolge des
   *  Bildes: obenauf zuerst, so wie die Zeitleiste sie zeigt. */
  const ebenen = useMemo(() => {
    const passende = timeline.tracks.filter((t) => (istTon ? t.kind === 'audio' : t.kind === 'video'));
    return istTon ? passende : [...passende].reverse();
  }, [timeline.tracks, istTon]);

  const [spurId, setSpurId] = useState<string>(() => ebenen[0]?.id ?? '');
  const [gewaehlt, setStelle] = useState<Stelle>('hier');
  const spur = timeline.tracks.find((t) => t.id === spurId) ?? ebenen[0];

  if (!medium || !spur) return null;

  const dauer = medium.dauer;
  const befunde: Record<Stelle, ReturnType<typeof stelleFuer>> = {
    hier: stelleFuer(spur, playhead, 'hier', dauer, fps),
    davor: stelleFuer(spur, playhead, 'davor', dauer, fps),
    danach: stelleFuer(spur, playhead, 'danach', dauer, fps),
  };
  // Nur die Stellen, die es auf dieser Ebene gibt und die frei sind (7.153).
  // Die Regel steht in `moeglicheStellen` und ist dort geprüft.
  const moeglich = moeglicheStellen(spur, playhead, dauer, fps);
  // Wechselt die Ebene und die gewählte Stelle gibt es dort nicht, gilt
  // wieder „am Abspielkopf“ -- die gibt es immer.
  const stelle: Stelle = moeglich.includes(gewaehlt) ? gewaehlt : 'hier';
  const befund = befunde[stelle];
  const start = befund.start;
  const konflikt = start === null ? null : konfliktBei(spur, start, dauer);
  const unten = darunterMoeglich(timeline.tracks, spur.id);
  const luecke = start === null ? null : freieStelle(start, dauer, sortedClips(spur));
  const grundspur = timeline.tracks.find((t) => t.kind === 'video');
  const obere = ebenen.filter((t) => t.kind === 'video' && t.id !== grundspur?.id);

  /**
   * **Die Ebene nach dem, was sie tut** -- nicht nach ihrem Namen.
   *
   * „Overlay“ und „Video 1“ sind Namen von Spuren; wer einfügt, will wissen,
   * ob das Bild *im* Film steht oder *über* ihm liegt. Der Name kommt nur
   * dazu, wenn es mehr als eine Ebene darüber gibt.
   */
  const ebeneHeisst = (t: Track): string => {
    if (t.kind !== 'video') return t.name || t.id;
    if (t.id === grundspur?.id) return 'Im Film';
    return obere.length > 1 ? `Über dem Bild · ${t.name || t.id}` : 'Über dem Bild';
  };

  /** Was passiert, in einem Satz -- das steht über dem Knopf. */
  const ergebnis = (t: Track, von: number): string => {
    const zeitraum = `von ${zeit(von)} bis ${zeit(von + dauer)}`;
    if (t.kind !== 'video') return `Läuft ${zeitraum} auf „${t.name || t.id}“.`;
    if (t.id === grundspur?.id) return `Steht ${zeitraum} im Film.`;
    return `Liegt ${zeitraum} über dem Bild.`;
  };

  const kurz = (text: string): string => (text.length > 14 ? `${text.slice(0, 13)}…` : text);
  const stellenName = (art: Stelle): string => {
    if (art === 'hier') return 'Am Abspielkopf';
    const bezug = befunde[art].bezug;
    const wer = bezug?.label ? `„${kurz(bezug.label)}“` : 'dem Clip';
    return art === 'davor' ? `Vor ${wer}` : `Nach ${wer}`;
  };

  const fertig = () => {
    setEinfuegeMedium(null);
    setEinfuegeStelle(null);
    onClose();
  };

  /** Legt den Clip -- entweder auf die gewählte Ebene oder auf eine neue. */
  const einfuegen = (ausweg: 'hier' | 'darueber' | 'darunter' | 'luecke') => {
    if (start === null) return;
    const ops: Op[] = [];
    let zielSpur = spur.id;
    let zielStart = start;

    if (ausweg === 'darueber' || ausweg === 'darunter') {
      const index = spurIndexFuer(timeline.tracks, spur.id, ausweg);
      if (index === null) return;
      const praefix = istTon ? 'A' : 'V';
      let n = 1;
      while (timeline.tracks.some((t) => t.id === `${praefix}${n}`)) n++;
      const neue = Track.parse({ id: `${praefix}${n}`, kind: istTon ? 'audio' : 'video' });
      ops.push({ op: 'addTrack', track: neue, index } as Op);
      zielSpur = neue.id;
    } else if (ausweg === 'luecke') {
      if (luecke === null) return;
      zielStart = luecke;
    }

    const clip = Clip.parse({
      id: newId('c'),
      source: { kind: 'asset', assetId: medium.assetId },
      start: zielStart,
      duration: dauer,
      label: medium.name,
    });
    ops.push(ausDatei({ op: 'addClip', trackId: zielSpur, clip } as Op));
    dispatch(ops);
    select([clip.id]);
    toast(
      'success',
      ausweg === 'darueber' || ausweg === 'darunter'
        ? `„${medium.name}“ auf einer neuen Ebene ${ausweg === 'darueber' ? 'darüber' : 'darunter'}, ab ${zeit(zielStart)}.`
        : `„${medium.name}“: ${ergebnis(spur, zielStart)}`,
    );
    fertig();
  };

  return (
    <Sheet title={`„${medium.name}“ einfügen`} onClose={fertig} closeLabel="Abbrechen">
      {/* Erst *wohin*, dann *wann*: welche Stellen es gibt, hängt von der
          Ebene ab. Andersherum gefragt, standen oben Knöpfe, die erst durch
          die Antwort darunter grau oder wählbar wurden. */}
      <div className="field">
        <span>Wohin?</span>
        <div className="chips" data-ebenen>
          {ebenen.map((t) => (
            <button
              key={t.id}
              className={`chip ${t.id === spur.id ? 'active' : ''}`}
              data-ebene={t.id}
              onClick={() => setSpurId(t.id)}
            >
              {ebeneHeisst(t)}
              {t.locked ? ' 🔒' : ''}
            </button>
          ))}
        </div>
      </div>

      <div className="field">
        <span>Ab wann?</span>
        <div className="chips" data-stellen>
          {moeglich.map((art) => (
            <button
              key={art}
              className={`chip ${stelle === art ? 'active' : ''}`}
              data-stelle={art}
              onClick={() => setStelle(art)}
            >
              {stellenName(art)}
              <span className="chip-zeit"> {zeit(befunde[art].start!)}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Und was dann passiert. Kein Prüfergebnis („Dort ist Platz“), sondern
          der Satz, den man vor dem Tippen wissen will. */}
      {start === null ? null : konflikt ? (
        <div className="card" style={{ margin: '0 0 12px' }} data-konflikt>
          <div className="title" style={{ color: 'var(--warn-text)' }}>
            {konflikt.label ? `Dort liegt schon „${konflikt.label}“` : 'Dort liegt schon ein Clip'}
          </div>
          <p className="hint" style={{ marginTop: 4 }}>
            Von {zeit(konflikt.start)} bis {zeit(konflikt.start + konflikt.duration)}. Wohin stattdessen?
          </p>
          <div className="stack" style={{ marginTop: 8 }}>
            <button className="primary" onClick={() => einfuegen('darueber')} data-ausweg="darueber">
              Darüber, auf eine neue Ebene
            </button>
            {unten.ok ? (
              <button onClick={() => einfuegen('darunter')} data-ausweg="darunter">
                Darunter, auf eine neue Ebene
              </button>
            ) : null}
            {luecke !== null ? (
              <button onClick={() => einfuegen('luecke')} data-ausweg="luecke">
                Später, in die nächste Lücke ab {zeit(luecke)}
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <>
          <p className="hint" style={{ margin: '4px 0 12px' }} data-konflikt="keiner" data-ergebnis>
            {ergebnis(spur, start)}
          </p>
          <button className="primary block" onClick={() => einfuegen('hier')} data-einfuegen>
            Einfügen
          </button>
        </>
      )}
    </Sheet>
  );
}
